import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, TriangleAlert, X, Loader2, FileText, ExternalLink } from 'lucide-react'
import { marked } from 'marked'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  useChatPendingChanges,
  useSubmitChatPendingChanges,
  useCancelChatPendingChanges,
  useRemoveChatPendingChangeItem,
} from '@/hooks'
import type { ChatPendingChangeSet, ChatPendingChangeSetItem } from '@/api/chatPendingChanges'
import { getErrorMessage } from '@/api/errors'
import { SanitizedHtml } from '@/components/SanitizedHtml'
import { cn } from '@/lib/utils'
import { clearImportExclusions, readImportExclusions, writeImportExclusions } from '@/lib/importExclusionsStorage'

const UPDATE_FIELD_ORDER = ['title', 'description', 'state', 'area_path', 'iteration_path', 'assigned_to']

const CREATE_FIELD_ORDER = ['area_path', 'iteration_path', 'assigned_to', 'parent_work_item_id']

const CREATE_FIELDS_RENDERED_SEPARATELY = new Set(['type', 'title', 'description'])

function parseValues(json: string | null): Record<string, string | null> {
  if (!json) return {}
  try {
    const parsed = JSON.parse(json) as unknown
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([key, value]) => [
        key,
        value === null || value === undefined ? null : typeof value === 'string' ? value : String(value),
      ]),
    )
  } catch {
    return {}
  }
}

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  area_path: 'Project',
  iteration_path: 'Sprint',
  parent_work_item_id: 'Parent',
  wiql: 'WIQL',
}

function formatFieldLabel(key: string): string {
  return FIELD_LABEL_OVERRIDES[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatFieldValue(key: string, value: string | null): string | null {
  if (key === 'parent_work_item_id') return value ? `#${value}` : null
  return value
}

const RICH_TEXT_FIELDS = new Set([
  'description',
  'Microsoft.VSTS.Common.AcceptanceCriteria',
  'Microsoft.VSTS.TCM.ReproSteps',
  'Microsoft.VSTS.TCM.SystemInfo',
])

// Chosen by field key rather than by sniffing the value: a work item's description reaches the card
// already flattened to plain text and entity-decoded by the backend, so markup in a before-value
// cannot be told apart from text somebody typed, and rendering it would drop what it shows.
function isRichTextField(key: string, side: 'before' | 'proposed'): boolean {
  if (!RICH_TEXT_FIELDS.has(key)) return false

  return !(side === 'before' && key === 'description')
}

const CODE_FIELDS = new Set(['wiql'])

const SHARED_QUERY_FIELD_ORDER = ['name', 'wiql']

function FieldValue({ value, rich, className }: { value: string; rich: boolean; className?: string }) {
  if (rich) return <SanitizedHtml html={value} className={className} />

  return <span className={cn('whitespace-pre-wrap break-words', className)}>{value}</span>
}

function orderedFieldKeys(values: Record<string, string | null>, order: string[]): string[] {
  return [...new Set([...order.filter((k) => k in values), ...Object.keys(values)])]
}

function rowTitle(item: ChatPendingChangeSetItem, allItems: ChatPendingChangeSetItem[]): string {
  if (item.changeType === 'Create') {
    const values = parseValues(item.proposedValuesJson)
    return values.type ? `New ${values.type}` : 'New work item'
  }
  if (item.changeType === 'Update') {
    return item.targetWorkItemId ? `Work item #${item.targetWorkItemId}` : 'Work item'
  }
  if (item.changeType === 'Import') {
    const values = parseValues(item.proposedValuesJson)
    return `${values.type ?? 'Work item'} #${item.targetWorkItemId}`
  }
  if (item.changeType === 'SharedQuery') {
    const values = parseValues(item.proposedValuesJson)
    return values.query_name ? `Shared query "${values.query_name}"` : 'Shared query'
  }
  if (item.changeType === 'WikiPage') {
    const values = parseValues(item.proposedValuesJson)
    return values.title ? `Wiki page "${values.title}"` : 'Wiki page'
  }
  return workItemRef(item.relatedWorkItemId, item.relatedPendingItemId, allItems)
}

function workItemRef(workItemId: number | null, pendingItemId: string | null, allItems: ChatPendingChangeSetItem[]): string {
  if (workItemId) return `#${workItemId}`
  if (pendingItemId) {
    const referenced = allItems.find((i) => i.id === pendingItemId)
    if (referenced) {
      const values = parseValues(referenced.proposedValuesJson)
      return values.title ? `${values.title} (new)` : 'New work item'
    }
    return 'New work item'
  }
  return '—'
}

function includeAriaLabel(item: ChatPendingChangeSetItem): string {
  const values = parseValues(item.proposedValuesJson)
  return `Include #${item.targetWorkItemId} "${values.title ?? ''}" in the import`
}

function removeAriaLabel(item: ChatPendingChangeSetItem, allItems: ChatPendingChangeSetItem[]): string {
  if (item.changeType === 'Create') {
    const values = parseValues(item.proposedValuesJson)
    if (values.title) return `Remove ${rowTitle(item, allItems)} "${values.title}"`
  }
  return `Remove ${rowTitle(item, allItems)} (item ${item.itemIndex + 1})`
}

function ChangeTypeBadge({ item }: { item: ChatPendingChangeSetItem }) {
  const config = {
    Create: 'bg-success text-success-foreground border border-success-foreground',
    Update: 'bg-warning text-warning-foreground border border-warning-foreground',
    Relationship: 'bg-info text-info-foreground border border-info-foreground',
    Import: 'bg-muted text-foreground border border-border',
    SharedQuery: 'bg-warning text-warning-foreground border border-warning-foreground',
    WikiPage: 'bg-info text-info-foreground border border-info-foreground',
  }[item.changeType]

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${config}`}>
      {CHANGE_TYPE_LABELS[item.changeType] ?? item.changeType}
    </span>
  )
}

const CHANGE_TYPE_LABELS: Partial<Record<ChatPendingChangeSetItem['changeType'], string>> = {
  SharedQuery: 'Shared query',
  WikiPage: 'Wiki page',
}

function CreateSection({ item }: { item: ChatPendingChangeSetItem }) {
  const values = parseValues(item.proposedValuesJson)
  const detailKeys = orderedFieldKeys(values, CREATE_FIELD_ORDER).filter(
    (key) => !CREATE_FIELDS_RENDERED_SEPARATELY.has(key) && !!values[key],
  )

  return (
    <div className="space-y-1">
      {values.title && <div className="text-xs font-medium">{values.title}</div>}
      {values.description && (
        <div className="text-xs text-muted-foreground">
          <FieldValue value={values.description} rich={isRichTextField('description', 'proposed')} />
        </div>
      )}
      {detailKeys.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {detailKeys.map((key) => (
            <div key={key} data-testid={`pending-change-${item.id}-field-${key}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {formatFieldLabel(key)}
              </div>
              <div className="text-xs">
                <FieldValue
                  value={formatFieldValue(key, values[key] ?? null) ?? ''}
                  rich={isRichTextField(key, 'proposed')}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function UpdateSection({ item }: { item: ChatPendingChangeSetItem }) {
  return <FieldDiffList item={item} keys={orderedFieldKeys(parseValues(item.proposedValuesJson), UPDATE_FIELD_ORDER)} />
}

function FieldDiffList({ item, keys }: { item: ChatPendingChangeSetItem; keys: string[] }) {
  const proposed = parseValues(item.proposedValuesJson)
  const before = parseValues(item.beforeValuesJson)

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {keys.map((key) => {
        const beforeValue = formatFieldValue(key, before[key] ?? null)
        const afterValue = formatFieldValue(key, proposed[key] ?? null)
        const beforeRich = isRichTextField(key, 'before')
        const afterRich = isRichTextField(key, 'proposed')
        const stacked = !!beforeValue && (beforeRich || afterRich || CODE_FIELDS.has(key))
        const valueClassName = CODE_FIELDS.has(key) ? 'font-mono' : undefined

        return (
          <div
            key={key}
            data-testid={`pending-change-${item.id}-field-${key}`}
            className={cn((stacked || afterRich) && 'w-full')}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{formatFieldLabel(key)}</div>
            {stacked ? (
              <div className="space-y-1 text-xs">
                <div>
                  <div className="text-[10px] font-semibold tracking-wider text-muted-foreground">Current</div>
                  <FieldValue value={beforeValue} rich={beforeRich} className={cn('text-muted-foreground', valueClassName)} />
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-wider text-muted-foreground">Proposed</div>
                  <FieldValue value={afterValue ?? ''} rich={afterRich} className={valueClassName} />
                </div>
              </div>
            ) : (
              <div className="text-xs">
                {beforeValue ? (
                  <>
                    <FieldValue value={beforeValue} rich={false} className="text-muted-foreground line-through" />
                    <span className="mx-1 text-muted-foreground">&rarr;</span>
                    <FieldValue value={afterValue ?? ''} rich={false} />
                  </>
                ) : (
                  <FieldValue value={afterValue ?? ''} rich={afterRich} className={valueClassName} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function RelationshipSection({ item, allItems }: { item: ChatPendingChangeSetItem; allItems: ChatPendingChangeSetItem[] }) {
  const values = parseValues(item.proposedValuesJson)
  const related = workItemRef(item.relatedWorkItemId, item.relatedPendingItemId, allItems)
  const target = workItemRef(item.targetWorkItemId, item.targetPendingItemId, allItems)
  const isUnlink = values.operation === 'unlink'

  return (
    <div className="flex items-center gap-1.5 text-xs" data-testid={`pending-change-relationship-${item.id}`}>
      <span className="font-medium">{related}</span>
      <span className="text-muted-foreground">{isUnlink ? '✕' : '→'}</span>
      <span className="font-medium">{target}</span>
      {values.link_type && <span className="text-[11px] text-muted-foreground">({values.link_type})</span>}
    </div>
  )
}

function SharedQuerySection({ item }: { item: ChatPendingChangeSetItem }) {
  const values = parseValues(item.proposedValuesJson)
  const url = values.url?.startsWith('https://') ? values.url : null
  const location = [values.team_project, values.path].filter(Boolean).join(' · ')

  return (
    <div className="space-y-1">
      {location && (
        <div className="text-xs text-muted-foreground" data-testid={`pending-change-${item.id}-source`}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
              {location}
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </a>
          ) : (
            location
          )}
        </div>
      )}
      <FieldDiffList item={item} keys={SHARED_QUERY_FIELD_ORDER.filter((key) => key in values)} />
    </div>
  )
}

const WIKI_PREVIEW_STYLES = [
  '[&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-3 [&_h1]:pb-1 [&_h1]:border-b [&_h1]:border-border [&_h1:first-child]:mt-0',
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:pb-1 [&_h2]:border-b [&_h2]:border-border/50',
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1',
  '[&_table]:my-2',
].join(' ')

function WikiPageSection({ item }: { item: ChatPendingChangeSetItem }) {
  const [showSource, setShowSource] = useState(false)
  const values = parseValues(item.proposedValuesJson)
  const url = values.url?.startsWith('https://') ? values.url : null
  const location = [values.team_project, values.wiki_name, values.page_path].filter(Boolean).join(' · ')
  const parent = !values.parent_path || values.parent_path === '/' ? 'the top level of the wiki' : values.parent_path
  const content = values.content ?? ''
  const renderedContent = useMemo(() => marked.parse(content, { async: false }), [content])

  return (
    <div className="space-y-1.5">
      {location && (
        <div className="text-xs text-muted-foreground" data-testid={`pending-change-${item.id}-source`}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
              {location}
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </a>
          ) : (
            location
          )}
        </div>
      )}
      <div className="text-xs" data-testid={`pending-change-${item.id}-mode`}>
        {values.mode === 'update' ? 'Replaces the current content of this page' : `New page under ${parent}`}
      </div>
      <div className="flex gap-1">
        <Button type="button" size="sm" variant={showSource ? 'outline' : 'default'} className="h-6 px-2 text-[11px]" aria-pressed={!showSource} onClick={() => setShowSource(false)}>
          Rendered
        </Button>
        <Button type="button" size="sm" variant={showSource ? 'default' : 'outline'} className="h-6 px-2 text-[11px]" aria-pressed={showSource} onClick={() => setShowSource(true)}>
          Markdown
        </Button>
      </div>
      <div
        role="region"
        aria-label="Wiki page content preview"
        tabIndex={0}
        className="max-h-80 overflow-y-auto rounded border bg-background p-3 text-xs"
      >
        {showSource ? (
          <pre className="whitespace-pre-wrap break-words font-mono">{content}</pre>
        ) : (
          <SanitizedHtml html={renderedContent} className={WIKI_PREVIEW_STYLES} />
        )}
      </div>
    </div>
  )
}

function wikiPageHeaderText(items: ChatPendingChangeSetItem[]): string {
  if (items.length === 1) {
    const title = parseValues(items[0].proposedValuesJson).title
    return title ? `Please confirm publishing "${title}" to the Azure DevOps wiki` : 'Please confirm publishing to the Azure DevOps wiki'
  }
  return `Please confirm publishing ${items.length} pages to the Azure DevOps wiki`
}

function sharedQueryHeaderText(items: ChatPendingChangeSetItem[]): string {
  if (items.length === 1) {
    const name = parseValues(items[0].proposedValuesJson).query_name
    return name ? `Please confirm the changes to the shared query "${name}"` : 'Please confirm the changes to the shared query'
  }
  return `Please confirm the changes to ${items.length} shared queries`
}

function isSkippedDuplicate(item: ChatPendingChangeSetItem): boolean {
  return item.changeType === 'Import' && item.isDuplicate
}

function isSelectableImport(item: ChatPendingChangeSetItem): boolean {
  return item.changeType === 'Import' && !item.applied && !item.isDuplicate
}

function workItemsLabel(count: number): string {
  return `${count} work item${count === 1 ? '' : 's'}`
}

function importHeaderText(newCount: number, duplicateCount: number): string {
  if (newCount === 0) {
    return `All ${workItemsLabel(duplicateCount)} from Azure DevOps ${duplicateCount === 1 ? 'is' : 'are'} already in IDSP`
  }

  const duplicateNote = duplicateCount > 0 ? ` (${duplicateCount} already in IDSP)` : ''
  return `Please confirm the import of ${workItemsLabel(newCount)} from Azure DevOps${duplicateNote}`
}

function ImportStatusTag({ item }: { item: ChatPendingChangeSetItem }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border',
        item.isDuplicate
          ? 'bg-warning text-warning-foreground border-warning-foreground'
          : 'bg-success text-success-foreground border-success-foreground',
      )}
      data-testid={`pending-change-${item.id}-import-status`}
    >
      {item.isDuplicate ? 'Duplicate' : 'New'}
    </span>
  )
}

function ImportSection({ item }: { item: ChatPendingChangeSetItem }) {
  const values = parseValues(item.proposedValuesJson)
  const source = [values.team_project, values.area_path, values.iteration_path].filter(Boolean).join(' · ')
  const url = values.url?.startsWith('https://') ? values.url : null

  return (
    <div className="space-y-1">
      {values.title && (
        <div className="text-xs font-medium">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
            >
              {values.title}
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </a>
          ) : (
            values.title
          )}
        </div>
      )}
      {source && (
        <div className="text-xs text-muted-foreground" data-testid={`pending-change-${item.id}-source`}>
          {source}
        </div>
      )}
      {item.isDuplicate && (
        <div className="text-xs text-muted-foreground">
          {item.existingWorkItemNumber != null
            ? `Already in IDSP as #${item.existingWorkItemNumber} — it will be skipped.`
            : 'Already in IDSP — it will be skipped.'}
        </div>
      )}
    </div>
  )
}

function PendingChangeRow({
  item,
  allItems,
  onRemove,
  isRemoving,
  disableRemove,
  canRemove,
  isExcluded,
  onToggleIncluded,
}: {
  item: ChatPendingChangeSetItem
  allItems: ChatPendingChangeSetItem[]
  onRemove: (itemId: string) => void
  isRemoving: boolean
  disableRemove: boolean
  canRemove: boolean
  isExcluded: boolean
  onToggleIncluded: (itemId: string) => void
}) {
  const isSelectable = isSelectableImport(item)

  return (
    <div
      className={cn('py-2', isExcluded && 'opacity-50')}
      data-testid={`pending-change-item-${item.id}`}
      role="group"
      aria-label={rowTitle(item, allItems)}
    >
      <div className="flex items-center gap-1.5">
        {isSelectable ? (
          <Checkbox
            aria-label={includeAriaLabel(item)}
            checked={!isExcluded}
            disabled={disableRemove}
            onCheckedChange={() => onToggleIncluded(item.id)}
          />
        ) : (
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold truncate">{rowTitle(item, allItems)}</span>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <ChangeTypeBadge item={item} />
          {item.changeType === 'Import' && !item.applied && <ImportStatusTag item={item} />}
          {item.applied && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-muted text-muted-foreground">
              Already applied
            </span>
          )}
          {canRemove && item.changeType !== 'Import' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={removeAriaLabel(item, allItems)}
              className="h-5 w-5 p-0 text-muted-foreground"
              disabled={isRemoving || disableRemove}
              onClick={() => onRemove(item.id)}
            >
              {isRemoving ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </div>
      <div className="mt-1 pl-5">
        {item.changeType === 'Create' && <CreateSection item={item} />}
        {item.changeType === 'Update' && <UpdateSection item={item} />}
        {item.changeType === 'Relationship' && <RelationshipSection item={item} allItems={allItems} />}
        {item.changeType === 'Import' && <ImportSection item={item} />}
        {item.changeType === 'SharedQuery' && <SharedQuerySection item={item} />}
        {item.changeType === 'WikiPage' && <WikiPageSection item={item} />}
        {item.error && <div className="mt-1 text-xs text-danger-foreground">{item.error}</div>}
      </div>
    </div>
  )
}

export function ChatPendingChangesCard({
  projectId,
  chatId,
}: {
  projectId: string
  chatId: string
}) {
  const { data: changeSet, isError, error } = useChatPendingChanges(projectId, chatId)
  const submitMutation = useSubmitChatPendingChanges(projectId, chatId)

  if (isError) {
    return (
      <div className="py-4 mx-auto my-1 w-full">
        <div
          className="flex items-center gap-2 rounded-lg border px-4 py-3 text-xs font-semibold bg-danger text-danger-foreground border-danger-foreground"
          data-testid="chat-pending-changes-error"
        >
          <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
          <span>{getErrorMessage(error) ?? "Couldn't check for pending Azure DevOps changes on this chat."}</span>
        </div>
      </div>
    )
  }

  const isActive = !!changeSet && (changeSet.status === 'Pending' || changeSet.isInterrupted)

  if (isActive) {
    return (
      <ChatPendingChangesCardInner
        key={changeSet.id}
        projectId={projectId}
        chatId={chatId}
        changeSet={changeSet}
        submitMutation={submitMutation}
      />
    )
  }

  return null
}

function ChatPendingChangesCardInner({
  projectId,
  chatId,
  changeSet,
  submitMutation,
}: {
  projectId: string
  chatId: string
  changeSet: ChatPendingChangeSet
  submitMutation: ReturnType<typeof useSubmitChatPendingChanges>
}) {
  const [expanded, setExpanded] = useState(false)
  const [removingItemId, setRemovingItemId] = useState<string | null>(null)
  const [excludedAzureDevOpsIds, setExcludedAzureDevOpsIds] = useState(() => readImportExclusions(changeSet.id))
  const cancelMutation = useCancelChatPendingChanges(projectId, chatId)
  const removeMutation = useRemoveChatPendingChangeItem(projectId, chatId)

  const { isInterrupted } = changeSet
  const activeItems = changeSet.items.filter((i) => !i.removedByUser)
  const referencedWorkItemIds = new Set(
    activeItems.flatMap((i) => [i.targetWorkItemId, i.relatedWorkItemId]).filter((id): id is number => id != null),
  )
  const hasNonWorkItemChanges = activeItems.some((i) => i.changeType === 'SharedQuery' || i.changeType === 'WikiPage')
  const summaryTarget =
    !hasNonWorkItemChanges && referencedWorkItemIds.size === 1
      ? `work item #${[...referencedWorkItemIds][0]}`
      : `${activeItems.length} item(s)`
  const isImportOnly = activeItems.length > 0 && activeItems.every((i) => i.changeType === 'Import')
  const isSharedQueryOnly = activeItems.length > 0 && activeItems.every((i) => i.changeType === 'SharedQuery')
  const isWikiPageOnly = activeItems.length > 0 && activeItems.every((i) => i.changeType === 'WikiPage')
  const pendingImports = activeItems.filter((i) => i.changeType === 'Import' && !i.applied)
  const newImportCount = pendingImports.filter((i) => !i.isDuplicate).length
  const headerText = isImportOnly
    ? importHeaderText(newImportCount, pendingImports.length - newImportCount)
    : isSharedQueryOnly
      ? sharedQueryHeaderText(activeItems)
      : isWikiPageOnly
        ? wikiPageHeaderText(activeItems)
        : `Please confirm the updates for ${summaryTarget}`
  const isExcluded = (item: ChatPendingChangeSetItem) =>
    isSelectableImport(item) && item.targetWorkItemId != null && excludedAzureDevOpsIds.has(item.targetWorkItemId)
  const excludedItems = activeItems.filter(isExcluded)
  const confirmedItems = activeItems.filter((i) => !isExcluded(i))
  const submittableCount = confirmedItems.filter((i) => !i.applied && !isSkippedDuplicate(i)).length
  const hasUnappliedConfirmedItem = confirmedItems.some((i) => !i.applied)

  const toggleIncluded = (itemId: string) => {
    const azureDevOpsId = activeItems.find((i) => i.id === itemId)?.targetWorkItemId
    if (azureDevOpsId == null) return

    setExcludedAzureDevOpsIds((current) => {
      const next = new Set(current)
      if (!next.delete(azureDevOpsId)) next.add(azureDevOpsId)
      writeImportExclusions(changeSet.id, next)
      return next
    })
  }

  const handleRemove = (itemId: string) => {
    setRemovingItemId(itemId)
    removeMutation.mutate(itemId, {
      onSettled: () => setRemovingItemId(null),
      onSuccess: (result) => {
        if (result.cascadeRemovedItemIds.length > 0) {
          toast.info(`Also removed ${result.cascadeRemovedItemIds.length} item(s) that depended on it.`)
        }
      },
    })
  }

  const actionsDisabled = cancelMutation.isPending || submitMutation.isPending || removeMutation.isPending

  return (
    <div className="py-4 mx-auto my-1 w-full">
      <div
        className={cn('border rounded-lg overflow-hidden', !expanded ? 'bg-danger border-danger-foreground' : 'bg-card')}
        data-testid="chat-pending-changes-card"
      >
        <button
          type="button"
          className={cn(
            'w-full flex items-center gap-2 px-4 py-3 text-left',
            !expanded ? '!bg-danger hover:!bg-danger/80 border-b border-danger' : '',
          )}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-xs font-semibold">
            {isInterrupted
              ? 'Some changes from this submission were not applied — please review and retry'
              : headerText}
          </span>
          <span className="ml-auto">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </button>

        {expanded && (
          <>
            <div className="border-t px-4 divide-y">
              {activeItems.map((item) => (
                <PendingChangeRow
                  key={item.id}
                  item={item}
                  allItems={changeSet.items}
                  onRemove={handleRemove}
                  isRemoving={removingItemId === item.id}
                  disableRemove={actionsDisabled}
                  canRemove={!item.applied}
                  isExcluded={isExcluded(item)}
                  onToggleIncluded={toggleIncluded}
                />
              ))}
              {activeItems.length === 0 && (
                <p className="py-2 text-xs italic text-muted-foreground">All proposed changes were removed.</p>
              )}
            </div>
            <div className="border-t px-4 py-2 flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                className="flex-1"
                disabled={!hasUnappliedConfirmedItem || actionsDisabled}
                onClick={() =>
                  submitMutation.mutate(
                    { confirmed: confirmedItems, excluded: excludedItems },
                    { onSuccess: () => clearImportExclusions(changeSet.id) },
                  )
                }
              >
                {submitMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {isInterrupted ? 'Retry' : submittableCount > 0 ? `Submit (${submittableCount})` : 'Submit'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex-1"
                disabled={actionsDisabled}
                onClick={() => cancelMutation.mutate(undefined, { onSuccess: () => clearImportExclusions(changeSet.id) })}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
